import { Node, mergeAttributes } from "@tiptap/core";

import { EditorMode, type CustomSignOptions } from "../interfaces";

import i18n from "@/Plugins/i18n";

import { hasValue } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";

import { getRandomId } from "./utils";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    SignBlock: {
      insertSign: () => ReturnType;
    };
  }
}

interface insertSignInputOptions {
  name?: string;
}

export const SignBlock = Node.create<CustomSignOptions>({
  name: "SignBlock",

  group: "block",
  atom: false,
  selectable: true,
  draggable: false,
  content: "inline*",

  addOptions() {
    return {
      mode: EditorMode.EDITOR,
      onClick: undefined,
    };
  },

  addAttributes() {
    return {
      mode: EditorMode.EDITOR,
      name: {
        default: `sign_${getRandomId()}`,
        parseHTML: (element) => element.getAttribute("data-name") || `sign_${getRandomId()}`,
        renderHTML: (attributes) => ({ "data-name": attributes.name }),
      },
      value: "",
      class: {
        default: "html_doc__sign",
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div.html_doc__sign",
        getAttrs: (el: HTMLElement) => {
          return hasValue(el)
            ? {
                name: el.getAttribute("data-name") ?? `sign_${getRandomId()}`,
              }
            : null;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes({}, { class: "html_doc__sign", ["data-name"]: HTMLAttributes["data-name"] }),
      0,
    ];
  },

  addNodeView() {
    return ({ node }) => {
      const wrapper = document.createElement("div");
      const contentLabel = document.createElement("span");

      if (this.options.mode === EditorMode.COMPLETE) {
        contentLabel.innerHTML = i18n.global.t("documents.editor.sign.complete.title");
      } else if (this.options.mode === EditorMode.VIEWER) {
        contentLabel.innerHTML = i18n.global.t("documents.editor.sign.viewer.title");
      } else if (this.options.mode === EditorMode.EDITOR) {
        contentLabel.innerHTML = i18n.global.t("documents.editor.sign.editor.title");
      }

      wrapper.className = "html_doc__sign";

      wrapper.setAttribute("data-name", node.attrs.name);
      wrapper.appendChild(contentLabel);

      const handleClick = () => {
        if (hasValue(this.options.onClick)) {
          this.options.onClick({ name: node.attrs.name, value: "" });
        }
      };

      if (this.options.mode === EditorMode.COMPLETE) {
        if (isValidString(node.attrs.value)) {
          const img = document.createElement("img");

          img.src = node.attrs.value;
          img.alt = "signature";

          wrapper.appendChild(img);
          wrapper.removeChild(contentLabel);
        }

        wrapper.addEventListener("click", handleClick);
      } else if (this.options.mode === EditorMode.VIEWER) {
        if (isValidString(node.attrs.value)) {
          const signDate = document.createElement("p");

          signDate.innerHTML = node.attrs.value;

          wrapper.appendChild(signDate);
          wrapper.removeChild(contentLabel);
        }
      }

      return {
        dom: wrapper,
        destroy: () => {
          wrapper.removeEventListener("click", handleClick);
        },
      };
    };
  },

  addCommands() {
    return {
      insertSign:
        (op: insertSignInputOptions = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { class: "html_doc__sign", name: op.name ?? `sign_${getRandomId()}` },
          });
        },
    };
  },
});

export default SignBlock;
